import { AssistantStore } from '@/ai/store/AssistantStore'
import { AssistantApi } from '@/ai/api/AssistantApi'
import AssistantSocket from '@/ai/websocket/AssistantSocket'
import { AiChatUserInput } from '@/ai/model/AiChatUserInput'
import AiChatUserInputType from '@/ai/model/AiChatUserInputType'
import { UUIDGeneratorBrowser } from '@/lib/utils/browser'
import AiChatSuggestionPage from '@/ai/model/AiChatSuggestionPage'

const AssistantService = {
  userId         : null,
  accountId      : null,
  companyType    : null,
  accountFeatures: [],
  chatUrl        : null,

  async initialize (userId, accountId, companyType, accountFeatures = [], chatUrl = null) {
    this.userId = userId
    this.accountId = accountId
    this.companyType = companyType
    this.accountFeatures = accountFeatures
    this.chatUrl = chatUrl
    await this.getChatGreeting()
  },

  async getRecentChatSessions () {
    AssistantStore.Sessions.Recent.Loading = true
    try {
      AssistantStore.Sessions.Recent.Data = await AssistantApi.getChatSessions(this.accountId, this.userId, 5)
    } finally {
      AssistantStore.Sessions.Recent.Loading = false
    }
  },

  async getAllChatSessions () {
    AssistantStore.Sessions.All.Loading = true
    try {
      AssistantStore.Sessions.All.Data = await AssistantApi.getChatSessions(this.accountId, this.userId)
    } finally {
      AssistantStore.Sessions.All.Loading = false
    }
  },

  async getChatGreeting () {
    AssistantStore.Greeting.Loading = true
    try {
      AssistantStore.Greeting.Data = await AssistantApi.getChatGreeting(this.accountId, this.userId)
    } finally {
      AssistantStore.Greeting.Loading = false
    }
  },

  async getChatSuggestions (page) {
    AssistantStore.Suggestions.Loading = true
    try {
      AssistantStore.Suggestions.Data = await AssistantApi.getChatSuggestions(this.accountId, page)
    } finally {
      AssistantStore.Suggestions.Loading = false
    }
  },

  async deleteChatSession (sessionId, openNewSession = true) {
    await AssistantApi.deleteChatSession(this.userId, this.accountId, sessionId)
    const selectedSession = AssistantStore.SelectedSession.Data
    const isSelectedSessionDeleted = selectedSession && selectedSession.sessionId === sessionId
    if (isSelectedSessionDeleted && openNewSession) {
      await this.newSession()
    }
    await this.getRecentChatSessions()
    await this.getAllChatSessions()
  },

  async onPageChange ({ to, from }) {
    const rawPath = to.path || ''
    const routePath = `/${rawPath.replace(/^\/[a-z]{2}(?=\/|$)/i, '').replace(/^\/+/, '')}`
    if (!this.userId || !this.accountId || to.name === 'Login' || !this.accountFeatures.includes('CORPORATE_AGENT')) {
      return
    }

    let page
    if (this.companyType === 'CORPORATE') {
      if (
        /^\/alerts(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_ALERTS')
      ) {
        page = AiChatSuggestionPage.CORPORATE_ALERTS
      } else if (
        /^\/risks(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_RISK')
      ) {
        page = AiChatSuggestionPage.CORPORATE_RISK
      } else if (
        /^\/hedging(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_HEDGING')
      ) {
        page = AiChatSuggestionPage.CORPORATE_HEDGING
      } else if (
        /^\/industry(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_INDUSTRY')
      ) {
        page = AiChatSuggestionPage.CORPORATE_INDUSTRY
      } else if (
        /^\/positions(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_POSITIONS')
      ) {
        page = AiChatSuggestionPage.CORPORATE_POSITIONS
      } else {
        page = AiChatSuggestionPage.CORPORATE_DASHBOARD
      }
    } else {
      if (
        /^\/banking\/alm(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('BANKING_ALM')
      ) {
        page = AiChatSuggestionPage.BANKING_ALM
      } else if (
        /^\/rates(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('BANKING_RATES')
      ) {
        page = AiChatSuggestionPage.BANKING_RATES
      } else if (
        /^\/risks(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('BANKING_RISK')
      ) {
        page = AiChatSuggestionPage.BANKING_RISK
      } else if (
        /^\/hedging(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('BANKING_HEDGING')
      ) {
        page = AiChatSuggestionPage.BANKING_HEDGING
      } else if (
        /^\/positions(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_POSITIONS')
      ) {
        page = AiChatSuggestionPage.CORPORATE_POSITIONS
      } else if (
        /^\/alerts(?:\/|$)/.test(routePath) &&
        this.accountFeatures.includes('CORPORATE_ALERTS')
      ) {
        page = AiChatSuggestionPage.CORPORATE_ALERTS
      } else {
        page = AiChatSuggestionPage.BANKING_DASHBOARD
      }
    }
    if (AssistantStore.Suggestions.Page === page) {
      return
    }
    AssistantStore.Suggestions.Page = page
    await this.getChatSuggestions(page)
  },

  async selectSession (session) {
    this.clearSession()
    AssistantStore.SelectedSession.Data = session
    AssistantStore.Messages.Data = await AssistantApi.getChatMessages(this.accountId, session.sessionId)
    AssistantSocket.connect(this.userId, this.accountId, session.sessionId)
  },

  async newSession () {
    this.clearSession()
    AssistantSocket.connect(this.userId, this.accountId)
  },

  sendMessage (text, type = AiChatUserInputType.MESSAGE, oldStreamId = null) {
    AssistantStore.Streaming.StopRequested = false
    AssistantStore.Streaming.AnimationActive = false
    AssistantStore.Streaming.DisplayedContent = ''
    const generatedStreamId = UUIDGeneratorBrowser()
    AssistantStore.Messages.Data.push({
      role    : 'USER',
      content : text,
      streamId: generatedStreamId
    })
    AssistantStore.Editing.Active = false
    const selectedSession = AssistantStore.SelectedSession.Data
    const recentSessions = AssistantStore.Sessions.Recent.Data || []

    if (selectedSession) {
      const existsInRecent = recentSessions.some(
        session => session.sessionId === selectedSession.sessionId
      )

      if (!existsInRecent) {
        AssistantStore.Sessions.Recent.Data = [
          selectedSession,
          ...recentSessions
        ].slice(0, 5)
      }
    }

    AssistantStore.Streaming.StreamId = generatedStreamId
    const userInput = new AiChatUserInput({
      type       : type,
      message    : text,
      streamId   : AssistantStore.Streaming.StreamId,
      oldStreamId: oldStreamId
    })
    AssistantSocket.send(JSON.stringify(userInput))
  },

  stopStreaming () {
    if (
      !this.userId ||
      !this.accountId
    ) {
      return
    }
    AssistantStore.Streaming.StopRequested = true

    const userInput = new AiChatUserInput({
      type    : AiChatUserInputType.STOP_STREAMING,
      message : AssistantStore.Streaming.DisplayedContent || '',
      streamId: AssistantStore.Streaming.StreamId
    })
    AssistantSocket.stopStreaming(userInput)
    AssistantStore.Streaming.StreamId = null
  },

  openSidebar () {
    AssistantStore.Sidebar.Open = true
  },

  closeSidebar () {
    AssistantStore.Sidebar.Open = false
  },

  clearSession () {
    AssistantSocket.close()
    AssistantStore.Sessions.Recent.Loading = false
    AssistantStore.Sessions.All.Loading = false
    AssistantStore.Greeting.Loading = false
    AssistantStore.Suggestions.Loading = false
    AssistantStore.SelectedSession.Data = null
    AssistantStore.Messages.Data.splice(0)
    AssistantStore.Messages.Loading = false
    AssistantStore.Messages.SendEnabled = true
    AssistantStore.Streaming.Active = false
    AssistantStore.Streaming.DisplayedContent = ''
    AssistantStore.Streaming.StreamId = null
    AssistantStore.Streaming.AnimationActive = false
    AssistantStore.Socket.Connected = false
    AssistantStore.Editing.Active = false
  },

  destroy () {
    AssistantSocket.close()
    AssistantStore.Sidebar.Open = false
    AssistantStore.Sessions.Recent.Loading = false
    AssistantStore.Sessions.Recent.Data.splice(0)
    AssistantStore.Sessions.All.Loading = false
    AssistantStore.Sessions.All.Data.splice(0)
    AssistantStore.SelectedSession.Data = null
    AssistantStore.Messages.Data.splice(0)
    AssistantStore.Messages.Loading = false
    AssistantStore.Messages.AutoScroll = true
    AssistantStore.Messages.SendEnabled = true
    AssistantStore.Streaming.Active = false
    AssistantStore.Streaming.AnimationActive = false
    AssistantStore.Streaming.DisplayedContent = ''
    AssistantStore.Streaming.StreamId = null
    AssistantStore.Streaming.StopRequested = false
    AssistantStore.Editing.Active = false
    AssistantStore.Socket.Connected = false
    AssistantStore.Greeting.Loading = false
    AssistantStore.Greeting.Data = null
    AssistantStore.Suggestions.Loading = false
    AssistantStore.Suggestions.Data.splice(0)
    AssistantStore.Suggestions.Page = null
  }

}

export default AssistantService
